import { type KeyboardEvent, useCallback, useState } from "react";
import { FiX } from "react-icons/fi";

interface TagsSectionProps {
  /** Current tags */
  value: string[];
  /** Callback when tags change */
  onChange: (tags: string[]) => void;
  /** Whether tags are required */
  required?: boolean;
  /** Custom label */
  label?: string;
  /** Help text */
  helpText?: string;
  /** Placeholder for input */
  placeholder?: string;
}

/**
 * TagsSection - Tag input with chip display
 *
 * Enter or comma to add tags, click X to remove.
 */
export function TagsSection({
  value,
  onChange,
  required = false,
  label = "Tags",
  helpText = "Enter tags for this media. Separate tags by commas.",
  placeholder = "Add a tag...",
}: TagsSectionProps) {
  const [input, setInput] = useState("");

  const addTag = useCallback(
    (tag: string) => {
      const trimmed = tag.trim().toLowerCase();
      if (trimmed && !value.includes(trimmed)) {
        onChange([...value, trimmed]);
      }
      setInput("");
    },
    [value, onChange]
  );

  const removeTag = useCallback(
    (tagToRemove: string) => {
      onChange(value.filter((tag) => tag !== tagToRemove));
    },
    [value, onChange]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" || e.key === ",") {
        e.preventDefault();
        addTag(input);
      } else if (e.key === "Backspace" && !input && value.length > 0) {
        // Remove last tag when backspace on empty input
        const lastTag = value[value.length - 1];
        if (lastTag) {
          removeTag(lastTag);
        }
      }
    },
    [input, value, addTag, removeTag]
  );

  const handleBlur = useCallback(() => {
    if (input.trim()) {
      addTag(input);
    }
  }, [input, addTag]);

  return (
    <div className="mb-6">
      {/* Label */}
      <label className="mb-2 block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>

      {/* Help text */}
      {helpText && <p className="mb-2 text-sm text-gray-500">{helpText}</p>}

      {/* Tags display */}
      {value.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {value.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-sm text-blue-800"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="rounded-full p-0.5 hover:bg-blue-200"
                aria-label={`Remove tag ${tag}`}
              >
                <FiX className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
    </div>
  );
}

export default TagsSection;
